import { X } from "lucide-react";
import { useClickOutside } from '../../hooks/useClickOutside';
import useEscapeKey from "../../hooks/useEscapeKey";
import { motion } from "framer-motion";

interface ConfirmDialogProps {
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  cancelAction?: () => void; // Optional specific action for the cancel button
  cancelIsTextDanger?: boolean; // Optional style for cancel button text
  isDanger?: boolean; // Style for the main confirm button (rightmost)
  onThirdOption?: () => void; // Optional handler for a third button
  thirdOptionText?: string; // Optional text for the third button
  thirdOptionColor?: 'primary' | 'secondary'; // Optional style for the third button
}

export default function ConfirmDialog({
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  cancelAction,
  cancelIsTextDanger = false,
  isDanger = false,
  onThirdOption,
  thirdOptionText,
  thirdOptionColor = 'primary', // Default to primary (blue)
}: ConfirmDialogProps) {
  // Handle the actual close
  const handleClose = () => {
    onClose(); // Directly call onClose
  };

  const dialogRef = useClickOutside<HTMLDivElement>(handleClose);
  useEscapeKey(handleClose); // Use escape key to close the dialog

  return (
    // AnimatePresence has been moved to the parent component (ProjectMenuDialogs.tsx)
    // This component now directly returns the motion.div
    <motion.div
        className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
        initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            ref={dialogRef}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[480px] mx-4 border border-gray-200 dark:border-gray-700"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Header */}
            <div className="px-6 pt-5 rounded-t-lg">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {title}
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300">{message}</p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 rounded-b-lg border-t border-gray-100 dark:border-gray-800">
              <div className="flex justify-end gap-3">
                {/* Cancel Button (Leftmost) */}
                <button
                  onClick={() => {
                    if (cancelAction) {
                      cancelAction();
                    }
                    handleClose(); // Always close the dialog
                  }}
                  className={`
                    px-4 py-2 text-sm font-medium 
                    ${cancelIsTextDanger 
                      ? 'text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400' 
                      : 'text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200'}
                  `}
                >
                  {cancelText}
                </button>
                {/* Optional Third Button (Middle) */}
                {onThirdOption && thirdOptionText && (
                   <button
                    onClick={() => {
                      if (onThirdOption) onThirdOption();
                      handleClose();
                    }}
                    className={`
                      px-4 py-2 text-sm font-medium rounded-md text-white
                      ${
                        thirdOptionColor === 'primary'
                          ? "bg-blue-600 hover:bg-blue-700" // Standard primary button
                          : "bg-gray-600 hover:bg-gray-700" // Secondary/neutral button
                      }
                    `}
                  >
                    {thirdOptionText}
                  </button>
                )}
                {/* Confirm Button (Rightmost) */}
                <button
                  onClick={() => {
                    onConfirm();
                    handleClose(); // Also close the dialog on confirm
                  }}
                  className={`
                px-4 py-2 text-sm font-medium rounded-md text-white
                ${
                  isDanger
                    ? "bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }
              `}
                >
                  {confirmText}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
  );
}
